export type RequestMethod = "Get" | "Post" | "Put" | "Delete" | "Patch";
export type RequestType = "JSON" | "Blob" | "FormData";
export type ResponseType = "JSON" | "Blob" | "ArrayBuffer";

export interface RequestOptions {
  requestType?: RequestType;
  responseType?: ResponseType;
  query?: { [key: string]: string | string[] | undefined; };
  headers?: { [key: string]: string | undefined; };
}

class RequestUtil {
  async call<Type = any>(url: string, method: RequestMethod, data?: any, options: RequestOptions = {}) {
    let requestType = options.requestType || "JSON";
    if (method === "Get") requestType = undefined;

    const responseType = options.responseType || "JSON";

    if (options.query) url += `?${this.formatQuery(options.query)}`;

    const xhr = new XMLHttpRequest();
    xhr.open(method.toUpperCase(), url, true);

    const headers = options.headers || {};
    const headerNames = Object.keys(headers);
    for (const headerName of headerNames) {
      const value = headers[headerName];
      xhr.setRequestHeader(headerName, value);
    }

    if (data) {
      if (requestType === "JSON") xhr.setRequestHeader("Content-Type", "application/json");
      xhr.send(
        requestType === "JSON" ?
          JSON.stringify(data) :
          data
      );
    } else {
      xhr.send();
    }

    xhr.responseType = responseType.toLowerCase() as XMLHttpRequestResponseType;

    return await new Promise<Type>((resolve, reject) => {
      xhr.addEventListener("load", () => {
        if (xhr.status >= 400 && xhr.status < 600) return reject(xhr.response);
        resolve(xhr.response);
      });
      xhr.addEventListener("error", (err) => {
        reject(err);
      });
    });
  }

  async retrieveJSON(url: string) {
    return await this.call(url, "Get", undefined, { responseType: "JSON" });
  }

  async retrieveBlob(url: string) {
    return await this.call(url, "Get", undefined, { responseType: "Blob" }) as Blob;
  }

  async uploadBlob(url: string, data: Blob) {
    await this.call(url, "Put", data, { requestType: "Blob" });
  }

  formatQuery(query: { [key: string]: string | string[] | undefined; }) {
    return Object
      .keys(query)
      .filter(key => query[key])
      .map(key => {
        const value = query[key];
        if (Array.isArray(value)) {
          return value
            .map(x => {
              return key + "[]=" + encodeURIComponent(x);
            })
            .join("&");
        }

        return key + "=" + encodeURIComponent(value as string);
      })
      .join("&");
  }
}

export default new RequestUtil();